import { normalizeColorHex } from '@/lib/utils';
import { mapClientPaymentTermsFromDTO } from '@/modules/clients/mappers/client-payment-terms.mapper';
import { mapClientStatusFromDTO } from '@/modules/clients/mappers/client-status.mapper';
import { mapInvoiceStatusFromDTO } from '@/modules/common/mappers/invoice-status.mapper';
import { mapOpsiumUserShortFromDTO } from '@/modules/team/mappers/opsium-user-short.mapper';

import {
  InvoiceAllowedAction,
  InvoiceDetails,
  InvoiceDetailsItem,
  InvoiceEntityType,
  InvoiceSyncStatus,
} from '../types';
import {
  InvoiceAllowedActionResponse,
  InvoiceDetailsItemResponse,
  InvoiceDetailsResponse,
  InvoiceSyncStatusResponse,
} from '../types/dto/response';
import {
  mapInvoiceSyncErrorFromResponse,
  mapInvoiceSyncOperationFromDTO,
} from './invoice-sync.mapper';

const mapInvoiceSyncStatusFromResponse = (status: InvoiceSyncStatusResponse): InvoiceSyncStatus => {
  switch (status) {
    case InvoiceSyncStatusResponse.SCHEDULED:
      return InvoiceSyncStatus.Scheduled;
    case InvoiceSyncStatusResponse.IN_PROGRESS:
      return InvoiceSyncStatus.InProgress;
    case InvoiceSyncStatusResponse.SYNCHRONIZED:
      return InvoiceSyncStatus.Synchronized;
    case InvoiceSyncStatusResponse.FAILED:
      return InvoiceSyncStatus.Failed;
  }
};

const mapInvoiceAllowedActionFromResponse = (
  action: InvoiceAllowedActionResponse
): InvoiceAllowedAction => {
  switch (action) {
    case InvoiceAllowedActionResponse.EDIT:
      return InvoiceAllowedAction.Edit;
    case InvoiceAllowedActionResponse.DELETE:
      return InvoiceAllowedAction.Delete;
    case InvoiceAllowedActionResponse.RETRY:
      return InvoiceAllowedAction.Retry;
  }
};

const mapInvoiceEntityTypeFromResponse = (
  entityType: InvoiceDetailsItemResponse['entityType']
): InvoiceEntityType => {
  switch (entityType) {
    case 'USER':
      return InvoiceEntityType.User;
    case 'UNKNOWN':
      return InvoiceEntityType.Unknown;
    case 'OVERHEAD':
      return InvoiceEntityType.Overhead;
    default:
      return InvoiceEntityType.Unknown;
  }
};

const mapInvoiceDetailsItemFromResponse = (
  response: InvoiceDetailsItemResponse
): InvoiceDetailsItem => {
  const entityType = mapInvoiceEntityTypeFromResponse(response.entityType);
  const isOverhead = entityType === InvoiceEntityType.Overhead;

  return {
    entityType,
    entityId: response.entityId,
    description: response.description,
    quantity: isOverhead ? '1' : response.quantity,
    unitPrice: isOverhead ? response.amount : response.unitPrice,
    amount: response.amount,
    member: response.member ? mapOpsiumUserShortFromDTO(response.member) : null,
  };
};

/** Maps the invoice details API response to the app's read-only invoice model. */
export const mapInvoiceDetailsFromResponse = (
  response: InvoiceDetailsResponse
): InvoiceDetails => ({
  id: response.id,
  externalId: response.externalId,
  referenceNumber: response.referenceNumber,
  status: mapInvoiceStatusFromDTO(response.status),
  syncStatus: mapInvoiceSyncStatusFromResponse(response.syncStatus),
  syncOperation: mapInvoiceSyncOperationFromDTO(response.syncOperation),
  syncError: mapInvoiceSyncErrorFromResponse(response.syncError),
  syncToken: response.syncToken,
  allowedActions: response.allowedActions.map(mapInvoiceAllowedActionFromResponse),
  project: response.project
    ? {
        id: response.project.id,
        name: response.project.name,
        status: response.project.status,
        colorHex: normalizeColorHex(response.project.colorHex) ?? null,
        logoUrl: response.project.logoUrl,
      }
    : null,
  client: {
    id: response.client.id,
    name: response.client.name,
    logoUrl: response.client.logoUrl,
    isDeleted: response.client.isDeleted,
    status: mapClientStatusFromDTO(response.client.status),
  },
  startDate: response.startDate,
  endDate: response.endDate,
  issuedDate: response.issuedDate,
  dueDate: response.dueDate,
  paymentTerm: response.paymentTerm ? mapClientPaymentTermsFromDTO(response.paymentTerm) : null,
  currencyCode: response.currencyCode,
  totalAmount: response.totalAmount,
  balance: response.balance,
  amountPaid: response.amountPaid,
  paymentDetailsId: response.paymentDetailsId,
  paymentMemo: response.paymentMemo,
  receiverEmails: { ...response.receiverEmails },
  items: response.items.map(mapInvoiceDetailsItemFromResponse),
});
